// src/pages/Usuarios.jsx
// Desenvolvido com IA by ChatGPT (OpenAI) + Projeto personalizado para Demetrios Garcia de Souza

import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, TextField, Grid, IconButton,
    TableSortLabel, Select, MenuItem, Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

function Usuarios() {
    const navigate = useNavigate();

    const [usuarios, setUsuarios] = useState([]);
    const [totalRegistros, setTotalRegistros] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Filtros
    const [busca, setBusca] = useState('');
    const [status, setStatus] = useState('');

    // Ordenação
    const [sortField, setSortField] = useState('id');
    const [sortOrder, setSortOrder] = useState('desc');

    // Busca dinâmica (debounce) apenas em busca
    useEffect(() => {
        const timeout = setTimeout(() => {
            setPage(1);
            carregarUsuarios(1);
        }, 350); // 350ms
        return () => clearTimeout(timeout);
        // eslint-disable-next-line
    }, [busca]);

    // Demais filtros e paginação/ordenacao
    useEffect(() => {
        carregarUsuarios();
        // eslint-disable-next-line
    }, [page, pageSize, sortField, sortOrder, status]);

    // Função para buscar usuários
    const carregarUsuarios = async (paginaManual) => {
        try {
            const params = {
                page: paginaManual || page,
                pageSize,
                busca,
                status,
                sortField,
                sortOrder
            };
            const response = await api.get('/usuarios', { params });
            setUsuarios(response.data.registros);
            setTotalRegistros(response.data.total);
        } catch (err) {
            console.error('Erro ao carregar usuários:', err);
            alert('Erro ao carregar usuários.');
        }
    };

    const handleFiltrar = () => {
        setPage(1);
        carregarUsuarios(1);
    };

    const handleLimpar = () => {
        setBusca('');
        setStatus('');
        setPage(1);
        setSortField('id');
        setSortOrder('desc');
        carregarUsuarios(1);
    };

    const handleBloquear = async (usuario) => {
        try {
            const novoStatus = usuario.status === 'ativo' ? 'inativo' : 'ativo';
            await api.put(`/usuarios/${usuario.id}/status`, { status: novoStatus });
            carregarUsuarios();
        } catch (err) {
            console.error('Erro ao atualizar status do usuário:', err);
            alert('Erro ao atualizar status do usuário.');
        }
    };

    const handleEditar = (id) => {
        navigate(`/usuarios/novo/${id}`);
    };

    const handleSort = (field) => {
        const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
        setSortField(field);
        setSortOrder(newOrder);
    };

    const calculaDiasRestantes = (dataVencimento) => {
        if (!dataVencimento) return '-';
        const hoje = new Date();
        const venc = new Date(dataVencimento);
        const diffTime = venc - hoje;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays >= 0 ? diffDays : 0;
    };

    // Bolinha verde/cinza para permissões binárias
    const BoolDot = ({ ativo, titleOn = 'Permissão ativa', titleOff = 'Sem permissão' }) => (
        <Tooltip title={ativo ? titleOn : titleOff}>
            <FiberManualRecordIcon
                sx={{
                    fontSize: 18,
                    color: ativo ? '#43a047' : '#bdbdbd',
                    verticalAlign: 'middle'
                }}
            />
        </Tooltip>
    );

    const totalPaginas = Math.ceil(totalRegistros / pageSize);

    return (
        <Box p={4}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">Usuários</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon />}
                    sx={{
                        backgroundColor: '#ffeb3b',
                        color: '#000',
                        borderRadius: '999px',
                        padding: '8px 20px',
                        fontWeight: 'bold',
                        boxShadow: '0px 2px 5px rgba(0,0,0,0.2)',
                        '&:hover': {
                            backgroundColor: '#fdd835'
                        }
                    }}
                    onClick={() => navigate('/usuarios/novo')}
                >
                    NOVO USUÁRIO
                </Button>
            </Box>

            {/* Filtros */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Nome ou Email ou Chave"
                            value={busca}
                            onChange={(e) => setBusca(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <TextField
                            select
                            label="Status"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            fullWidth
                        >
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="ativo">Ativo</MenuItem>
                            <MenuItem value="inativo">Inativo</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={2} display="flex" gap={1}>
                        <Button variant="contained" color="primary" onClick={handleFiltrar} fullWidth>
                            FILTRAR
                        </Button>
                        <Button variant="outlined" color="secondary" onClick={handleLimpar} fullWidth>
                            LIMPAR
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <TableContainer component={Paper}>
                <Table sx={{
                    '& thead th': {
                        backgroundColor: '#1976d2',
                        color: 'white',
                        fontWeight: 'bold',
                        padding: '6px 8px',
                        fontSize: '13px'
                    },
                    '& tbody td': {
                        padding: '6px 8px',
                        fontSize: '13px',
                        lineHeight: '1.4'
                    },
                    '& tbody tr:nth-of-type(odd)': {
                        backgroundColor: '#f9f9f9'
                    },
                    '& tbody tr:nth-of-type(even)': {
                        backgroundColor: '#ffffff'
                    }
                }}>
                    <TableHead>
                        <TableRow>
                            {[
                                { label: 'Código', field: 'chave' },
                                { label: 'Nome', field: 'nome' },
                                { label: 'Email', field: 'email' },
                                { label: 'Telefone', field: 'telefone' },
                                { label: 'Tipo', field: 'tipo' },
                                { label: 'Cadastro', field: 'criado_em' },
                                { label: 'Renovação', field: 'data_renovacao' },
                                { label: 'Vencimento', field: 'data_vencimento' },
                                { label: 'Dias Restantes', field: 'dias_restantes' },
                                { label: 'Kit Blindagem', field: 'pode_usar_kit_blindagem' },
                                // 🔹 NOVA COLUNA CRÉDITOS
                                { label: 'Créditos', field: 'creditos_kit_blindagem' },
                                { label: 'Webhook', field: 'pode_usar_webhook' },
                                { label: 'Status', field: 'status' },
                                { label: 'Ações', field: '' }
                            ].map((col) => (
                                <TableCell
                                    key={col.label}
                                    align={col.label === 'Nome' || col.label === 'Email' ? 'left' : 'center'}
                                    style={col.label === 'Código'
                                        ? {
                                            minWidth: 110,
                                            maxWidth: 120,
                                            fontFamily: 'monospace',
                                            fontWeight: 600
                                        }
                                        : {}}
                                >
                                    {col.field ? (
                                        <TableSortLabel
                                            active={sortField === col.field}
                                            direction={sortField === col.field ? sortOrder : 'asc'}
                                            onClick={() => handleSort(col.field)}
                                        >
                                            {col.label}
                                        </TableSortLabel>
                                    ) : col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {usuarios.map((usuario) => {
                            const diasRestantes = calculaDiasRestantes(usuario.data_vencimento);
                            return (
                                <TableRow key={usuario.id}>
                                    <TableCell
                                        align="center"
                                        style={{
                                            fontFamily: 'monospace',
                                            minWidth: 110,
                                            maxWidth: 120,
                                            fontWeight: 600
                                        }}
                                    >
                                        {usuario.chave}
                                    </TableCell>
                                    <TableCell align="left">{usuario.nome}</TableCell>
                                    <TableCell align="left">{usuario.email}</TableCell>
                                    <TableCell align="center">{usuario.telefone}</TableCell>
                                    <TableCell align="center">{usuario.tipo}</TableCell>
                                    <TableCell align="center">
                                        {usuario.criado_em
                                            ? usuario.criado_em.split('T')[0].split('-').reverse().join('/')
                                            : '-'}
                                    </TableCell>
                                    <TableCell align="center">
                                        {usuario.data_renovacao
                                            ? usuario.data_renovacao.split('T')[0].split('-').reverse().join('/')
                                            : '-'}
                                    </TableCell>
                                    <TableCell align="center">
                                        {usuario.data_vencimento
                                            ? usuario.data_vencimento.split('T')[0].split('-').reverse().join('/')
                                            : '-'}
                                    </TableCell>
                                    <TableCell align="center">{diasRestantes}</TableCell>

                                    {/* Kit Blindagem */}
                                    <TableCell align="center">
                                        <BoolDot
                                            ativo={usuario.pode_usar_kit_blindagem === 1 || usuario.pode_usar_kit_blindagem === true}
                                            titleOn="Kit Blindagem liberado"
                                            titleOff="Kit Blindagem não liberado"
                                        />
                                    </TableCell>

                                    {/* Créditos */}
                                    <TableCell align="center" style={{ fontWeight: 700 }}>
                                        {usuario.creditos_kit_blindagem ?? 0}
                                    </TableCell>

                                    {/* Webhook */}
                                    <TableCell align="center">
                                        <BoolDot
                                            ativo={usuario.pode_usar_webhook === 1 || usuario.pode_usar_webhook === true}
                                            titleOn="Webhook liberado"
                                            titleOff="Webhook não liberado"
                                        />
                                    </TableCell>

                                    <TableCell align="center">
                                        <Tooltip title={usuario.status === 'ativo' ? "Ativo" : "Inativo"}>
                                            <span style={{ fontSize: 24 }}>
                                                {usuario.status === 'ativo' ? "🟢" : "🔴"}
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box display="flex" justifyContent="center" gap={1}>
                                            <IconButton
                                                onClick={() => handleBloquear(usuario)}
                                                color="warning"
                                                size="small"
                                            >
                                                {usuario.status === 'ativo' ? <LockOpenIcon /> : <LockIcon />}
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleEditar(usuario.id)}
                                                color="primary"
                                                size="small"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Rodapé */}
            <Box
                mt={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography>Mostrar:</Typography>
                    <Select
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(e.target.value);
                            setPage(1);
                        }}
                        size="small"
                    >
                        {[10, 25, 50, 100, 250, 500].map((size) => (
                            <MenuItem key={size} value={size}>{size}</MenuItem>
                        ))}
                    </Select>
                </Box>
                <Box
                    display="flex"
                    alignItems="center"
                    gap={2}
                    justifyContent="center"
                    flex="1"
                >
                    <Button
                        variant="outlined"
                        onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                        disabled={page === 1}
                    >
                        Anterior
                    </Button>
                    <Typography>Página {page} de {totalPaginas}</Typography>
                    <Button
                        variant="outlined"
                        onClick={() => setPage((prev) => Math.min(prev + 1, totalPaginas))}
                        disabled={page === totalPaginas || totalPaginas === 0}
                    >
                        Próxima
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default Usuarios;
